/**
 * Frontmatter Form Component
 * 
 * Form for editing blog post frontmatter (metadata).
 */

import React from 'react';
import {
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { BlogPostInput } from '../../types/admin.types';

interface FrontmatterFormProps {
  /** Blog post data */
  blog: BlogPostInput;
  /** Update handler */
  updateField: <K extends keyof BlogPostInput>(
    field: K,
    value: BlogPostInput[K]
  ) => void;
}

const FrontmatterForm: React.FC<FrontmatterFormProps> = ({
  blog,
  updateField,
}) => {
  const [categoryInput, setCategoryInput] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');

  /**
   * Handle adding a category
   */
  const handleAddCategory = () => {
    if (categoryInput.trim()) {
      const categories = blog.categories || [];
      if (!categories.includes(categoryInput.trim())) {
        updateField('categories', [...categories, categoryInput.trim()]);
      }
      setCategoryInput('');
    }
  };

  /**
   * Handle removing a category
   */
  const handleRemoveCategory = (category: string) => {
    const categories = blog.categories || [];
    updateField(
      'categories',
      categories.filter((c) => c !== category)
    );
  };

  /**
   * Handle adding a tag
   */
  const handleAddTag = () => {
    if (tagInput.trim()) {
      const tags = blog.tags || [];
      if (!tags.includes(tagInput.trim())) {
        updateField('tags', [...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  /**
   * Handle removing a tag
   */
  const handleRemoveTag = (tag: string) => {
    const tags = blog.tags || [];
    updateField('tags', tags.filter((t) => t !== tag));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Post Metadata</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={blog.title}
                onChange={(e) => updateField('title', e.target.value)}
                required
                variant="outlined"
              />
            </Grid>

            {/* Slug */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Slug (URL-friendly)"
                value={blog.slug || ''}
                onChange={(e) => updateField('slug', e.target.value)}
                helperText="Auto-generated from title if left empty"
                variant="outlined"
              />
            </Grid>

            {/* Excerpt */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Excerpt"
                value={blog.excerpt || ''}
                onChange={(e) => updateField('excerpt', e.target.value)}
                multiline
                rows={3}
                helperText="Short summary for previews"
                variant="outlined"
              />
            </Grid>

            {/* Published Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Published Date"
                type="date"
                value={blog.publishedDate || ''}
                onChange={(e) => updateField('publishedDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                required
                variant="outlined"
              />
            </Grid>

            {/* Featured Image */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Featured Image URL"
                value={blog.featuredImage || ''}
                onChange={(e) => updateField('featuredImage', e.target.value)}
                placeholder="/images/..."
                variant="outlined"
              />
            </Grid>

            {/* Categories */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Categories
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {blog.categories?.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    onDelete={() => handleRemoveCategory(category)}
                    color="primary"
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Add category"
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCategory();
                  }
                }}
                InputProps={{
                  onBlur: handleAddCategory,
                }}
                variant="outlined"
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {blog.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="secondary"
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Add tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                InputProps={{
                  onBlur: handleAddTag,
                }}
                variant="outlined"
              />
            </Grid>

            {/* Checkboxes */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blog.isFeatured || false}
                      onChange={(e) =>
                        updateField('isFeatured', e.target.checked)
                      }
                    />
                  }
                  label="Featured Post"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blog.status === 'published'}
                      onChange={(e) =>
                        updateField('status', e.target.checked ? 'published' : 'draft')
                      }
                    />
                  }
                  label="Published"
                />
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default FrontmatterForm;

